import { createPortal } from "react-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/solid";
import ResponseMessage from "./ResponseMessage";

interface ActionDialogProps {
  isOpen: boolean;
  title?: string;
  message: string;
  actionColor?: string;
  proceedLabel?: string; // default "Proceed"
  cancelLabel?: string; // default "Cancel"
  onProceed: () => void;
  onCancel: () => void;
  // optional backend error message to show inside the dialog
  errorMessage?: string | null;
}

export default function ActionDialog({
  isOpen,
  title = "Confirm Action",
  message,
  actionColor = "black",
  proceedLabel = "Proceed",
  cancelLabel = "Cancel",
  onProceed,
  onCancel,
  errorMessage,
}: ActionDialogProps) {
  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 flex justify-center items-center z-[9998]"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}
      />

      {/* Dialog */}
      <div className="fixed inset-0 flex justify-center items-center z-[9999]">
        <div
          className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4"
          style={{ boxShadow: `0 0 25px ${actionColor} inset, 0 0 25px black` }}
        >
          <div className="flex items-center mb-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500 mr-2" />
            <h2 className="text-xl font-semibold">{title}</h2>
          </div>

          <p className="mb-3 py-1">{message}</p>
          {errorMessage && (
            <div className="mb-4">
              <ResponseMessage
                variant="error"
                message={errorMessage}
                // icon={<ExclamationTriangleIcon className="w-9 h-9" />}
              />
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={onCancel}
              className="flex items-center gap-2 px-4 py-2 rounded border border-gray-400 hover:bg-gray-100 cursor-pointer"
            >
              <XMarkIcon className="w-4 h-4" />
              {cancelLabel}
            </button>

            {(() => {
              const disabled = !!errorMessage;
              return (
                <button
                  onClick={() => {
                    if (!disabled) onProceed();
                  }}
                  disabled={disabled}
                  aria-disabled={disabled}
                  className={`flex items-center gap-2 px-4 py-2 rounded text-black ${
                    disabled
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:opacity-80 cursor-pointer"
                  }`}
                  style={{ backgroundColor: actionColor }}
                >
                  <CheckIcon className="w-4 h-4" />
                  {proceedLabel}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
